import React, { PropsWithChildren } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { MinusIcon, PlusIcon } from '../../assets';
import { breakpointMap, theme } from '../../theme';
import { BodyText } from '../typography';
import { BaseAccordion } from './BaseAccordion';
import { AccordionSize } from './types';

export type AccordionIconLeftProps = {
  open: boolean;
  size: AccordionSize;
  title: string;
  toggleOpen: () => void;
};

type sizeStyle = {
  header: FlattenSimpleInterpolation;
  content: FlattenSimpleInterpolation;
};

const smallStyles: sizeStyle = {
  header: css`
    padding: ${theme.spacings[2].rem} ${theme.spacings[3].rem};
  `,
  content: css`
    padding: ${theme.spacings[2].rem} ${theme.spacings[4].rem}
      ${theme.spacings[4].rem};
  `,
};

const largeStyles: sizeStyle = {
  header: css`
    padding: ${theme.spacings[3].rem};
  `,
  content: css`
    padding: ${theme.spacings[1].rem} 0 ${theme.spacings[5].rem}
      ${theme.spacings[8].rem};
  `,
};

const accordionSizeStyles = {
  xs: smallStyles,
  sm: smallStyles,
  md: largeStyles,
  lg: largeStyles,
};

const sizeStyles = (key: keyof sizeStyle, size: AccordionSize) =>
  breakpointMap(
    size,
    (accordionSize) => accordionSizeStyles[accordionSize][key]
  );

const Wrapper = styled.div`
  &:first-of-type {
    border-top: 1px solid ${theme.colors.neutrals.solidGrays[7]};
  }
`;

const Header = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('header', size)};
  align-items: center;
  display: flex;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.colors.neutrals.solidGrays[8]};
  }
`;

const Minus = styled(MinusIcon)`
  fill: ${theme.colors.primary.graysGray};
  margin-left: -3px;
  margin-right: ${theme.spacings[4].rem};
  width: 20px;
`;

const Plus = styled(PlusIcon)`
  fill: ${theme.colors.primary.graysGray};
  margin-right: ${theme.spacings[4].rem};
  width: 17px;
`;

const Title = styled(BodyText)`
  font-weight: 700;
`;

const Content = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('content', size)};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.neutrals.solidGrays[7]};
  margin: 0;
  width: 100%;
`;

export const AccordionIconLeft: React.FC<
  PropsWithChildren<AccordionIconLeftProps>
> = ({ children, open, size, title, toggleOpen }) => (
  <Wrapper>
    <BaseAccordion
      header={
        <Header size={size}>
          {open ? <Minus /> : <Plus />}
          <Title size="lg">{title}</Title>
        </Header>
      }
      onToggleOpen={toggleOpen}
      open={open}
    >
      <Content size={size}>{children}</Content>
    </BaseAccordion>
    <Divider />
  </Wrapper>
);
